import * as R from 'ramda';
import React, {createContext, FC, useCallback, useContext, useEffect, useState} from 'react';
import type {components} from '../../shared/api/v1/openapi.types';
import {ICommonComponentProps} from '../../definitions/common.definitions';
import {useMessage} from '../message/message-provider';
import axios from '../../transport/axios';
import {
  authUpdatedEventName,
  AuthUpdatedDetail,
  clearStoredAuth,
  getStoredAccessToken,
  getStoredAuth,
  getStoredRefreshToken,
  isAccessTokenExpired,
  updateStoredAuth,
} from './auth-storage';

type AuthResponseDto = components['schemas']['AuthResponseDto'];

export type AuthContextT = {
  token?: string;
  refreshToken?: string;
  user?: AuthResponseDto;
  storeToken: (value: string) => void;
  storeAuth: (value: AuthResponseDto) => void;
  clearToken: () => void;
  refreshTokens: () => Promise<AuthResponseDto | undefined>;
  logout: (opts?: { redirect?: boolean }) => void;
  isAuthenticated: boolean;
  openSignInModal: () => void;
  closeSignInModal: () => void;
  openSignUpModal: () => void;
  closeSignUpModal: () => void;
  openPasswordResetModal: () => void;
  closePasswordResetModal: () => void;
  signinVisible: boolean;
  signupVisible: boolean;
  passwordResetVisible: boolean;
}

const context = createContext<AuthContextT>(null!);
const AuthContextProvider = context.Provider;
const useAuth = () => useContext(context);

const AuthProvider: FC<ICommonComponentProps> = (props) => {
  const {
    children,
  } = props;
  const {
    showMessage,
  } = useMessage();
  const [token, setToken] = useState<string | undefined>(() => getStoredAccessToken());
  const [refreshToken, setRefreshToken] = useState<string | undefined>(() => getStoredRefreshToken());
  const [user, setUser] = useState<AuthResponseDto | undefined>(() => getStoredAuth());
  const [signinVisible, setSigninVisible] = useState(false);
  const [signupVisible, setSignupVisible] = useState(false);
  const [passwordResetVisible, setPasswordResetVisible] = useState(false);

  const openSignInModal = () => {
    setSigninVisible(true);
  }

  const closeSignInModal = () => {
    setSigninVisible(false);
  }

  const openSignUpModal = () => {
    setSignupVisible(true);
  }

  const closeSignUpModal = () => {
    setSignupVisible(false);
  }

  const openPasswordResetModal = () => {
    setPasswordResetVisible(true);
  }

  const closePasswordResetModal = () => {
    setPasswordResetVisible(false);
  }

  const storeToken = (value: string) => {
    setToken(value);
    localStorage.setItem('token', value);
  }

  const storeAuth = useCallback((value: AuthResponseDto) => {
    setUser(value);
    setToken(value.access_token);
    setRefreshToken(value.refresh_token);
    updateStoredAuth(value);
  }, []);

  const clearToken = useCallback(() => {
    setToken(undefined);
    setUser(undefined);
    setRefreshToken(undefined);
    clearStoredAuth();
  }, []);

  const refreshTokens = useCallback(async () => {
    const currentRefreshToken = refreshToken ?? getStoredRefreshToken();
    if (!currentRefreshToken) {
      return undefined;
    }
    const response = await axios.post<AuthResponseDto>(
      '/auth/refresh',
      JSON.stringify({ refresh_token: currentRefreshToken }),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    storeAuth(response.data);
    return response.data;
  }, [refreshToken, storeAuth]);

  const logout = useCallback((opts?: { redirect?: boolean }) => {
    const {
      redirect = true,
    } = opts ?? {};
    const currentRefreshToken = refreshToken ?? getStoredRefreshToken();
    if (currentRefreshToken) {
      axios.post(
        '/auth/logout',
        JSON.stringify({ refresh_token: currentRefreshToken }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      ).catch(() => undefined);
    }
    clearToken();
    showMessage({
      message: 'Вы вышли из аккаунта',
    });
    if (redirect && typeof window !== 'undefined') {
      window.location.assign('/');
    }
  }, [clearToken, refreshToken, showMessage]);

  const isAuthenticated = !R.isNil(token) && !isAccessTokenExpired(token);

  useEffect(() => {
    if (token && isAccessTokenExpired(token)) {
      refreshTokens().catch(() => {
        clearToken();
      });
    }
  }, [clearToken, refreshTokens, token]);

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [logout]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleUpdated = (event: Event) => {
      const detail = (event as CustomEvent<AuthUpdatedDetail>).detail;
      if (detail?.cleared) {
        setToken(undefined);
        setRefreshToken(undefined);
        setUser(undefined);
        return;
      }
      if (detail?.access_token) {
        setToken(detail.access_token);
      }
      if (detail?.refresh_token) {
        setRefreshToken(detail.refresh_token);
      }
      if (detail?.auth) {
        setUser(detail.auth);
      }
    };
    window.addEventListener(authUpdatedEventName, handleUpdated);
    return () => {
      window.removeEventListener(authUpdatedEventName, handleUpdated);
    };
  }, []);

  const ctx: AuthContextT = {
    token,
    refreshToken,
    user,
    storeToken,
    storeAuth,
    clearToken,
    refreshTokens,
    logout,
    isAuthenticated,
    openSignInModal,
    closeSignInModal,
    openSignUpModal,
    closeSignUpModal,
    openPasswordResetModal,
    closePasswordResetModal,
    signinVisible,
    signupVisible,
    passwordResetVisible,
  }

  return (
    <AuthContextProvider
      value={ctx}
    >
      {children}
    </AuthContextProvider>
  );
}

export default AuthProvider;

export {
  AuthProvider,
  useAuth,
}
